import React, { useMemo } from 'react';
import { createChatStrategy } from '@varos/assistant-react-chat';
import { assert, assertNever } from '@varos/util-typescript';
import { ContextItem, ThreadContextItem } from '@varos/assistant-sdk';
import { IntegrationContainerBaseConfig } from '../../../base';
import {
    ThreadDetailContainerProps,
    ThreadDetailViewProps,
} from './integrationDetailProps';
import { ThreadDetailController, ThreadDetailLoader } from './integrationDetailInterface';
import { ThreadCommonViewProps } from '../common';
import { AssistantContext } from '@varos/assistant-react';
import { useDisclosure } from '@chakra-ui/react';
import { buildMessageContext } from './integrationDetailFactory';
import { ThreadContextValues } from '../../../../chat';

export function createThreadDetailContainer(
    config: IntegrationContainerBaseConfig,
    loader: ThreadDetailLoader,
    controller: ThreadDetailController,
    View: React.FC<ThreadCommonViewProps>
): React.FC<ThreadDetailContainerProps> {
    const {
        kernel: {
            infra: {
                toaster: { useToast },
            },
        },
        repository,
        context: {
            useContext,
            chat: {
                thread: { Provider: ThreadContextProvider },
            },
        },
        hook,
    } = config;

    // Configure chat strategy with auto-reply enabled
    const { useChat } = createChatStrategy({
        autoReply: {
            enabled: true,
            // Use default condition: last message is from user AND no assistant messages exist
        },
    });

    return config.state.composer.subscribe(({ ...containerProps }) => {
        const toast = useToast();
        const context = useContext();
        const messagesContainerRef = React.useRef<HTMLDivElement>(null);
        const state = {
            composer: config.state.composer.useState(context),
        };

        const fetcher = {
            document: repository.research.document.useFetcher(context),
        };

        const mutation = {
            message: {
                create: repository.assistant.local.message.useCreate(context),
                update: repository.assistant.local.message.useUpdate(context),
            },
            thread: {
                update: repository.assistant.local.thread.useUpdate(context),
            },
        };

        const data = loader.useLoad(context, containerProps);
        // console.log('messages', data.thread.messages);

        // TODO encapsulate token access
        const auth = hook.useAuth();
        assert(auth.scheme.kind === 'legacy', 'legacy auth scheme is required');
        assert(auth.scheme.store.authToken, 'auth token is required');

        const chat = useChat(context, {
            options: {
                mode: 'solution',
                environmentMode: state.composer.value.test ? 'test' : 'live',
                tag: state.composer.value.version,
                workspace: context.workspace.id,
            },
            threadState: 'active',
            threadId: data.thread.thread.id,
            token: auth.scheme.store.authToken,
            onGenerated: () => {
                console.log('onGenerated');
            },
            messages: useMemo(
                () => data.thread.messages.map((msg) => msg.message),
                [data.thread.messages]
            ),
            async onMessageCreate(message) {
                console.log('onMessageCreate', message);
                assert(data.thread, 'thread is required');
                for (const part of message.parts) {
                    // TODO clean this up
                    if (
                        part.type === 'tool-invocation' &&
                        part.toolInvocation.toolName === 'start_solution' &&
                        part.toolInvocation.result
                    ) {
                        for (const artifact of part.toolInvocation.result.artifacts) {
                            if (artifact.kind === 'document') {
                                console.log('preloading document', artifact.id);
                                // Prefetch document to avoid triggering suspense
                                const document = await fetcher.document.fetch({
                                    contentId: artifact.id,
                                });
                                console.log('document preloaded', document.title);
                            }
                        }
                    }
                }
                const response = await mutation.message.create.mutateAsync({
                    id: message.id,
                    thread: data.thread.thread.id,
                    role: message.role,
                    parts: message.parts,
                    context: message.context,
                });
                console.log('message created', response);
            },
            async onMessageUpdate(message) {
                console.log('updating message', message);
                // const response = await mutation.message.update.mutateAsync({
                //     id: message.id,
                //     parts: message.parts,
                // });
                // console.log('message updated successfully', response);
            },
            async onCreateDocument(args) {
                console.log('creating document', args);
                // TODO: implement document creation for integration context
                return {
                    id: 'placeholder-document-id',
                };
            },
            async onReplaceDocument(args) {
                console.log('replacing document', args);
                // TODO: implement document replacement for integration context
            },
        });

        const disclosure = {
            call: useDisclosure(),
            request: useDisclosure(),
        };

        // Use context items from thread data directly and persist changes
        const contextItems = data.thread.thread.context || [];

        const handleContextItemsChange = async (newItems: ThreadContextItem[]) => {
            console.log('updating thread context', newItems);
            try {
                await mutation.thread.update.mutateAsync([
                    data.thread.thread.id,
                    { context: newItems },
                ]);
            } catch (error) {
                console.error('Error updating thread context:', error);
                toast({
                    kind: 'error',
                    description: 'Failed to update context',
                });
            }
        };

        const props = controller.useProps(context, data, {
            disclosure,
            composer: {
                async onSubmit(params) {
                    try {
                        const context = buildMessageContext(data);
                        console.log('sending message via chat.append', {
                            text: params.text,
                            context,
                        });
                        const response = await chat.append({
                            parts: [
                                {
                                    type: 'text',
                                    text: params.text,
                                },
                            ],
                            context,
                        });
                        console.log('chat response', response);
                    } catch (error) {
                        console.error('Error sending message:', error);
                        toast({
                            kind: 'error',
                            description: 'Failed to send message',
                        });
                    }
                },
                placeholder: `Message ${data.definition.display_name || 'Agent'}...`,
                context: contextItems.filter(
                    // TODO better handling of differentiation between public and hidden context
                    (item) => item.kind !== 'integration'
                ),
                onContextChange: handleContextItemsChange,
            },
            async onCall() {
                console.log('opening on call');
                disclosure.request.onOpen();
                // const context = buildMessageContext(data);
                // const result = await chat.reply({
                //     context,
                //     parts: [
                //         {
                //             type: 'text',
                //             text: 'User requested a call with autosubmit enabled',
                //         },
                //     ],
                // });
                // console.log('result', result);
            },
            onCallEnd(call) {
                console.log('call ended', call);
                disclosure.request.onClose();
            },
            messagesContainerRef,
        });

        const value: ThreadContextValues = useMemo(() => {
            return {
                chat,
            };
        }, [chat]);

        return (
            <ThreadContextProvider value={value}>
                <View {...props.common} />
            </ThreadContextProvider>
        );
    });
}
